import { useEffect, useState } from 'react';
import { useSessionStore } from '@/store/sessionStore';
import { cmd } from '@/session/sessionRuntime';
import { ROLE_LABEL, type GameEndResult } from '@/protocol/types';

const RESULT_LABEL: Record<GameEndResult, string> = {
  good_win: '好人阵营胜利',
  wolf_win: '狼人阵营胜利',
  aborted: '本局已中止',
};

const RESULT_CLASS: Record<GameEndResult, string> = {
  good_win: 'text-emerald-700 bg-emerald-50',
  wolf_win: 'text-red-700 bg-red-50',
  aborted: 'text-gray-600 bg-gray-100',
};

const AUTO_RETURN_SEC = 30;

export function EndView(): JSX.Element {
  const endResult = useSessionStore((s) => s.endResult);
  const revealedRoles = useSessionStore((s) => s.revealedRoles);
  const selfToken = useSessionStore((s) => s.self?.token ?? null);
  const word = useSessionStore((s) => s.word);
  const result = useSessionStore((s) => s.result);
  const swipeStats = result?.swipeStats ?? null;

  const [secondsLeft, setSecondsLeft] = useState<number>(AUTO_RETURN_SEC);

  useEffect(() => {
    if (secondsLeft <= 0) {
      cmd.returnLobby();
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const resultKey: GameEndResult = endResult ?? 'aborted';

  return (
    <main
      data-testid="end-view"
      className="mx-auto flex max-w-md flex-col gap-4 p-4"
    >
      <h1 className="text-center text-xl font-bold text-pig-700">本局结束</h1>

      <section
        data-testid="end-result"
        className={`rounded-lg p-4 text-center text-lg font-bold shadow ${RESULT_CLASS[resultKey]}`}
      >
        {RESULT_LABEL[resultKey]}
        {word && (
          <div data-testid="end-word" className="mt-2 text-sm font-normal opacity-80">
            本局词：<span className="font-semibold">{word}</span>
          </div>
        )}
      </section>

      {swipeStats && (
        <section
          data-testid="end-swipe-stats"
          className="rounded-lg bg-white p-3 text-center text-sm text-gray-700 shadow"
        >
          <span className="mr-2 text-xs text-gray-500">本局共</span>
          <span className="font-semibold text-pig-700">{swipeStats.total}</span>
          <span className="ml-1 text-xs text-gray-500">次滑动</span>
          {swipeStats.durationMs > 0 && (
            <span className="ml-3 text-xs text-gray-500">
              · {(swipeStats.durationMs / 1000).toFixed(0)}s · {swipeStats.perSecond.toFixed(2)}/s
            </span>
          )}
        </section>
      )}

      <section className="rounded-lg bg-white p-4 shadow">
        <div className="mb-2 text-xs text-gray-500">身份揭示</div>
        {revealedRoles.length === 0 ? (
          <div className="py-4 text-center text-gray-400">无身份信息</div>
        ) : (
          <ul data-testid="revealed-roles" className="flex flex-col gap-2">
            {revealedRoles.map((r) => {
              const isSelf = selfToken != null && r.token === selfToken;
              return (
                <li
                  key={r.token}
                  data-testid={`revealed-${r.token}`}
                  className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${
                    isSelf ? 'border-pig-300 bg-pig-50' : 'border-gray-100'
                  }`}
                >
                  <span className="font-medium text-gray-800">
                    {r.nickname || r.token}
                    {isSelf && (
                      <span className="ml-1 text-xs text-pig-600">（你）</span>
                    )}
                    {r.isChief && (
                      <span className="ml-2 rounded bg-amber-200 px-1.5 py-0.5 text-xs text-amber-900">
                        村长
                      </span>
                    )}
                  </span>
                  <span className="text-gray-600">
                    {ROLE_LABEL[r.baseRole as keyof typeof ROLE_LABEL] ?? r.baseRole}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <button
        data-testid="end-return-btn"
        onClick={() => cmd.returnLobby()}
        className="h-12 w-full rounded-lg bg-pig-600 text-base font-semibold text-white shadow"
      >
        返回大厅（{secondsLeft}s）
      </button>
    </main>
  );
}
